import { yupResolver } from '@hookform/resolvers/yup';
import { Modal, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import Flex from 'components/common/Flex';
import { FormProvider, RHFControl, RHFSelect } from 'components/common/form';
import LoadingButton from 'components/common/LoadingButton';

export const DONOR_DOCUMENT_TYPES = [
  'Direct Debit Mandate',
  'Standing Order Form',
  'Gift Aid Declaration',
  'Pledge Form',
  'Proof of Identity',
  'Proof of Address',
  'Signed Letter / Correspondence',
  'Receipt / Bank Statement',
  'Other'
];

const documentTypeOptions = DONOR_DOCUMENT_TYPES.map(type => ({
  label: type,
  value: type
}));

const MAX_SIZE_MB = 10;

const schema = Yup.object().shape({
  documentType: Yup.string().required('Please select a document type'),
  notes: Yup.string().nullable()
});

function DonorDocumentUploadModal({ show, onHide, onSubmit, loading }) {
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { documentType: '', notes: '' },
    resolver: yupResolver(schema)
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (!show) {
      reset();
      setFile(null);
      setFileError('');
    }
  }, [show, reset]);

  const handleFileChange = e => {
    const selected = e.target.files?.[0];
    if (!selected) {
      setFile(null);
      return;
    }
    if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
      setFileError(`File must be ${MAX_SIZE_MB}MB or smaller`);
      setFile(null);
      return;
    }
    setFileError('');
    setFile(selected);
  };

  const submit = values => {
    if (!file) {
      setFileError('Please select a file');
      return;
    }
    onSubmit({
      documentType: values.documentType,
      notes: values.notes?.trim() || null,
      file
    });
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Upload Donor Document</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormProvider methods={methods} onSubmit={handleSubmit(submit)}>
          <Flex gap={3} direction="column" className="mt-2">
            <RHFSelect
              name="documentType"
              label="Document Type"
              placeholder="Select a document type"
              options={documentTypeOptions}
              required
              noOptional
            />

            <Form.Group>
              <Form.Label>File</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                onChange={handleFileChange}
              />
              <Form.Text className="text-muted">
                Accepted: PDF, DOC, DOCX, PNG, JPG. Max {MAX_SIZE_MB}MB.
              </Form.Text>
              {fileError && (
                <div className="text-danger fs--1 mt-1">{fileError}</div>
              )}
            </Form.Group>

            <RHFControl
              name="notes"
              label="Notes"
              as="textarea"
              rows={2}
              placeholder="e.g. Signed 12 Oct 2026, first collection 1 Nov"
            />

            <LoadingButton
              type="submit"
              color="primary"
              className="w-100"
              loading={loading}
            >
              Upload
            </LoadingButton>
          </Flex>
        </FormProvider>
      </Modal.Body>
    </Modal>
  );
}

DonorDocumentUploadModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default DonorDocumentUploadModal;
